import React, { useEffect, useState, useCallback } from "react";
import usePersistentState from "../../../../hooks/usePersistentState";
import { Link } from "react-router-dom";
import {
  useMediaQuery,
  Button,
  Box,
  Paper,
  Typography,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
  Tooltip,
  Card
} from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import { tokens } from "../../../../theme";
import * as adminUserAction from "../../../../actions/admin/user";
import { countries } from "country-list-json";
import SortIcon from '@mui/icons-material/Sort';
import SearchIcon from '@mui/icons-material/Search';
import { getProfileImageUrl } from "../../../../utils/serverUtils";
import { useAppContext } from "../../../../Context/UseContext";
import moment from "moment";

const Contacts = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:768px)");
  const { showFullPhoneNumberemail } = useAppContext();

  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = usePersistentState("admin_user_list_page", 0);
  const [pageSize, setPageSize] = usePersistentState("admin_user_list_pageSize", 25);
  const [rowCount, setRowCount] = useState(0);
  const [exportLoading, setExportLoading] = useState(false);
  
  const [filterModel, setFilterModel] = usePersistentState("admin_user_list_filterModel", { items: [] });
  const [selectionModel, setSelectionModel] = useState([]); // Selection state usually resets
  
  // Custom Filters & Sort
  const [selectedCountry, setSelectedCountry] = usePersistentState("admin_user_list_country", null);
  const [searchText, setSearchText] = usePersistentState("admin_user_list_search", "");
  const [debouncedSearch, setDebouncedSearch] = useState(searchText); // Initialize with persisted searchText
  const [selectedGender, setSelectedGender] = usePersistentState("admin_user_list_gender", "");
  const [sortModel, setSortModel] = usePersistentState("admin_user_list_sort", [{ field: 'created_at', sort: 'desc' }]);

  // Debouce for search
  useEffect(() => {
    if (debouncedSearch === searchText) return;
    
    const timer = setTimeout(() => {
        setDebouncedSearch(searchText);
        setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const getApprovalStatus = (approval, deleted_at) => {
    if (deleted_at) return "Deleted";
    const statusMap = {
      10: "Pending",
      15: "Disable",
      20: "Approved",
      25: "Update Requested",
      30: "Rejected",
      40: "Incomplete Registration",
    };
    return statusMap[approval] || "N/A";
  };

  const containsOperator = getGridStringOperators().find(
    op => op.value === "contains"
  );

  const handleExportActiveUsers = async () => {
    try {
      setExportLoading(true);
      const response = await adminUserAction.exportActiveUsers();
      if (response.download_url) {
        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = `active-users-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error("Download URL not found in response");
      }
    } catch (error) {
      console.error("Error exporting active users:", error);
      alert("Failed to export active users. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const isClientFilter = selectedCountry || selectedGender;
      const fetchPageSize = isClientFilter ? 5000 : pageSize;
      const fetchPageNo = isClientFilter ? 1 : page + 1;

      const queryParams = new URLSearchParams({
        pageNo: fetchPageNo,
        limit: fetchPageSize,
      });

      if (debouncedSearch !== "") {
        queryParams.append('search', debouncedSearch);
      }

      // Maintain server-side filter support if user types in column filters (optional)
      filterModel.items.forEach(filter => {
        if (filter.value && filter.value !== "") {
          queryParams.append(`filter[${filter.columnField}]`, filter.value);
        }
      });

      const response = await adminUserAction.getCustomerLists(queryParams);

      let filteredResults = response.results;

      // Client-side filtering
      if (selectedGender) {
        filteredResults = filteredResults.filter(item => item.gender == selectedGender);
      }
      if (selectedCountry) {
         filteredResults = filteredResults.filter(item => item.country && item.country.includes(selectedCountry.name));
      }

      const formattedData = filteredResults.map(item => ({
        id: item.user_id,
        name: item.first_name ? `${item.first_name} ${item.last_name || ""}` : 'N/A',
        approval: item.approval,
        approval_text: getApprovalStatus(item.approval, item.deleted_at),
        age: item.birthday ? new Date().getFullYear() - new Date(item.birthday).getFullYear() : 'N/A',
        phone: item.phone || "N/A",
        email: item.email || "N/A",
        country: item.country || "N/A",
        gender: item.gender === 1 ? "Men" : item.gender === 2 ? "Women" : "Non-Binary",
        birthday: item.birthday, 
        created_at: item.created_at,
        avatar: { hash: item.hash, extension: item.extension, type: item.type }, // Pass full object
      }));

      // Client-side Sorting
      if (sortModel.length > 0) {
        const { field, sort } = sortModel[0];
        formattedData.sort((a, b) => {
          let aValue = a[field];
          let bValue = b[field];

          // Handle dates
          if (field === 'created_at' || field === 'birthday') {
              aValue = new Date(aValue || 0).getTime();
              bValue = new Date(bValue || 0).getTime();
          }
          // Handle strings
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();
          // Handle numbers (age)
          if (field === 'age' && typeof aValue === 'string') return 1; // move N/A to end

          if (aValue < bValue) return sort === 'asc' ? -1 : 1;
          if (aValue > bValue) return sort === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setCustomers(formattedData);
      setRowCount(isClientFilter ? formattedData.length : response.total);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
        setLoading(false);
    }
  }, [page, pageSize, filterModel, selectedGender, selectedCountry, debouncedSearch, sortModel]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClearFilters = () => {
    setSelectedCountry(null);
    setSearchText("");
    setDebouncedSearch("");
    setSelectedGender("");
    setSortModel([{ field: 'created_at', sort: 'desc' }]);
    setPage(0);
  };

  const desktopColumns = [

    {
      flex: 1.5,
      field: "name",
      headerName: "Name",
      filterOperators: [containsOperator],
      renderCell: (params) => (
          <Typography variant="body2" fontWeight="bold">
              {params.value}
          </Typography>
      )
    },
    {
      flex: 1,
      field: "approval_text",
      headerName: "Status",
      filterable: false,
      renderCell: (params) => {
        const status = params.value;
        let color = "default";
        if (status === "Approved") color = "success";
        else if (status === "Pending") color = "warning";
        else if (status === "Rejected") color = "error";
        
        return (
            <Chip 
               label={status} 
               color={color} 
               size="small" 
               variant="outlined"
            />
        );
      }
    },
    {
      flex: 0.8,
      field: "age",
      headerName: "Age",
      filterable: false,
    },
    {
      flex: 1.2,
      field: "phone",
      headerName: "Phone",
      filterOperators: [containsOperator],
      renderCell: params => (
        <Typography variant="body2">
           {showFullPhoneNumberemail ? params.value : params.value}
        </Typography>
      ),
    },
    {
      flex: 1.5,
      field: "email",
      headerName: "Email",
      filterOperators: [containsOperator],
       renderCell: params => (
        <Typography variant="body2">
           {showFullPhoneNumberemail ? params.value : params.value}
        </Typography>
      ),
    },
    {
      flex: 1,
      field: "gender",
      headerName: "Gender",
      filterable: false,
    },
    {
      flex: 1,
      field: "country",
      headerName: "Location",
      filterable: false,
    },
    {
      flex: 1.2,
      field: "created_at",
      headerName: "Registered",
      filterable: false,
      valueFormatter: (params) => {
        if (!params.value || params.value === "N/A") return "N/A";
        return moment(params.value).format("MMM D, YYYY");
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.8,
      filterable: false,
      renderCell: params => (
        <Button
          component={Link}
          to={`/admin/user/detail/${params.row.id}`}
          variant="contained"
          size="small"
          sx={{
            backgroundColor: colors.blueAccent[500],
            '&:hover': { backgroundColor: colors.blueAccent[700] },
            textTransform: 'none',
             borderRadius: '8px'
          }}
        >
          Details
        </Button>
      ),
    },
  ];

  const mobileColumns = [ // Retain standard mobile columns
     { field: "name", headerName: "Name", flex: 1 },
     { field: "email", headerName: "Email", flex: 1 },
      {
      field: "action",
      headerName: "Action",
      flex: 0.7,
      renderCell: params => (
         <Button
          component={Link}
          to={`/admin/user/detail/${params.row.id}`}
          size="small"
         >
             Details
         </Button>
      ),
    },
  ];

  return (
    <div className="container mt-4">
        {/* Header Removed as per pattern? Or kept? List/index.jsx had Header. Reports removed it. Approval has it. I'll keep a Title but style it. */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
            <Box>
                 <Typography variant="h2" color={colors.grey[100]} fontWeight="bold" sx={{ mb: '5px' }}>
                    Users List
                </Typography>
                <Typography variant="h5" color={colors.greenAccent[400]}>
                    Manage all users
                </Typography>
            </Box>
            <Button
                variant="contained"
                onClick={handleExportActiveUsers}
                disabled={exportLoading}
                sx={{
                    backgroundColor: colors.blueAccent[700],
                    color: colors.grey[100],
                    fontSize: "14px",
                    fontWeight: "bold",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    "&:hover": {
                        backgroundColor: colors.blueAccent[600],
                    },
                }}
            >
                {exportLoading ? 'Exporting...' : 'Export Active Users'}
            </Button>
        </Box>

      {/* Filter Bar */}
      <Paper 
        elevation={3}
        sx={{ 
            p: 2, 
            mb: 3, 
            backgroundColor: colors.primary[400], 
            borderRadius: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
        }}
      >
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {/* Search Bar */}
            <TextField
                variant="outlined"
                size="small"
                placeholder="Search User..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[400] }} />,
                    sx: { borderRadius: '12px', bgcolor: theme.palette.background.default, width: 250 }
                }}
            />

            {/* Gender Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Gender</InputLabel>
                <Select
                    value={selectedGender}
                    label="Gender"
                    onChange={(e) => {
                        setSelectedGender(e.target.value);
                        setPage(0);
                    }}
                    sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                >
                    <MenuItem value=""><em>Any Gender</em></MenuItem>
                    <MenuItem value="1">Men</MenuItem>
                    <MenuItem value="2">Women</MenuItem>
                </Select>
            </FormControl>

            {/* Location Filter */}
            <Autocomplete
                options={countries}
                getOptionLabel={(option) => option.name}
                value={selectedCountry}
                onChange={(event, newValue) => {
                    setSelectedCountry(newValue);
                    setPage(0);
                }}
                sx={{ width: 220 }}
                renderInput={(params) => (
                    <TextField 
                        {...params} 
                        label="Location" 
                        variant="outlined" 
                        size="small"
                        sx={{ 
                            '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: theme.palette.background.default }
                        }}
                    />
                )}
            />
            
            {(selectedCountry || selectedGender || searchText !== "") && (
                <Button onClick={handleClearFilters} color="error" size="small">
                    Clear
                </Button>
            )}
        </Box>

        <Box display="flex" gap={2} alignItems="center">
             {/* Sort By */}
            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Sort by</InputLabel>
                <Select
                    value={sortModel[0]?.field || "created_at"}
                    label="Sort by"
                    onChange={(e) => {
                        setSortModel([{ field: e.target.value, sort: sortModel[0]?.sort || 'desc' }]);
                        setPage(0);
                    }}
                    startAdornment={<SortIcon sx={{ mr: 1, fontSize: 18, color: colors.grey[400] }} />}
                    sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                >
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="approval_text">Status</MenuItem>
                    <MenuItem value="age">Age</MenuItem>
                    <MenuItem value="country">Location</MenuItem>
                    <MenuItem value="created_at">Registered</MenuItem>
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Order</InputLabel>
                <Select
                    value={sortModel[0]?.sort || "desc"}
                    label="Order"
                    onChange={(e) => {
                        setSortModel([{ field: sortModel[0]?.field || 'created_at', sort: e.target.value }]);
                        setPage(0);
                    }}
                    sx={{ borderRadius: '12px', bgcolor: theme.palette.background.default }}
                >
                    <MenuItem value="asc">Ascending</MenuItem>
                    <MenuItem value="desc">Descending</MenuItem>
                </Select>
            </FormControl>
        </Box>
      </Paper>

      <Box 
        sx={{ 
            height: 700, 
            width: '100%',
            '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: colors.primary[400],
                borderRadius: '16px',
                padding: '10px'
            },
            '& .MuiDataGrid-cell': {
                borderBottom: 'none',
                paddingY: '10px'
            },
            '& .MuiDataGrid-columnHeaders': {
                backgroundColor: 'transparent',
                borderBottom: `1px solid ${colors.grey[800]}`,
                color: colors.grey[300],
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
            },
            '& .MuiDataGrid-row': {
                borderRadius: '8px',
                marginTop: '5px',
                '&:hover': {
                        backgroundColor: colors.blueAccent[800]
                }
            },
             '& .MuiDataGrid-footerContainer': {
                borderTop: 'none',
                justifyContent: 'center'
            },
             '& .MuiCheckbox-root': {
                color: `${colors.grey[500]} !important`,
            },
        }}
      >
        <DataGrid
          page={page}
          loading={loading}
          pageSize={pageSize}
          rows={customers}
          rowCount={rowCount}
          pagination
          paginationMode={(selectedCountry || selectedGender) ? "client" : "server"}
          sortingMode="client"
          rowsPerPageOptions={[25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          columns={isMobile ? mobileColumns : desktopColumns}
          onPageChange={newPage => setPage(newPage)}
          onPageSizeChange={newPageSize => setPageSize(newPageSize)}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => setFilterModel(newModel)}
          getRowId={row => row.id}
          componentsProps={{
            pagination: {
              sx: {
                color: theme.palette.text.primary,
                "& .MuiTablePagination-selectLabel": { color: theme.palette.text.primary },
                "& .MuiTablePagination-input": { color: theme.palette.text.primary },
                "& .MuiTablePagination-displayedRows": { color: theme.palette.text.primary },
                "& .MuiSvgIcon-root": { color: theme.palette.text.primary },
                "& .MuiButtonBase-root": { color: theme.palette.text.primary },
                "& .MuiPaginationItem-root": { color: theme.palette.text.primary },
              },
            },
          }}
        />
      </Box>
    </div>
  );
};

export default Contacts;
